import * as path from "node:path";
import { Project, ProjenrcFile } from "projen/lib";

/**
 * Minimal shape projen's `TypescriptConfig` exposes for reading back the
 * final `include` array. We duck-type against it because non-TypeScript
 * projects don't have `tsconfig` at all.
 */
interface TypescriptConfigLike {
  readonly include: ReadonlyArray<string>;
}

interface WithTsconfig {
  readonly tsconfig?: TypescriptConfigLike;
}

interface WithSrcdir {
  readonly srcdir?: string;
}

/**
 * For each detected project, read its `tsconfig.include` and prefix each
 * entry with the project's outdir relative to the root. Returns a
 * deduplicated, sorted array of scoped glob patterns suitable for use as a
 * rule's `filePatterns`.
 *
 * Projects without a `tsconfig` fall back to `<relOutdir>/<srcdir>/**\/*.ts`
 * (srcdir defaults to `"src"`), which matches projen's defaults for any
 * plain `TypeScriptProject`.
 *
 * A TypeScript `.projenrc.ts` is additionally surfaced from projen's
 * `Projenrc` component: projen registers it in eslint's `allowDefaultProject`
 * and keeps it out of every `tsconfig.include`, so it would otherwise be
 * invisible to the tsconfig-driven scoping above.
 */
export function scopeFilePatternsToProjects(
  root: Project,
  detected: ReadonlyArray<Project>,
): Array<string> {
  const patterns = new Set<string>();

  for (const project of detected) {
    const rel = path.relative(root.outdir, project.outdir);
    const include = readTsconfigInclude(project);

    if (include) {
      // The project has a tsconfig — use its `include` verbatim. An
      // explicitly empty include (e.g. the MonorepoProject shell root,
      // which drops projen's default `<srcdir>/**\/*.ts`) intentionally
      // contributes no patterns. Only a project with no tsconfig at all
      // falls back to the default src glob below.
      for (const entry of include) {
        patterns.add(prefix(rel, entry));
      }
    } else {
      const srcdir = (project as WithSrcdir).srcdir ?? "src";
      patterns.add(prefix(rel, path.posix.join(srcdir, "**/*.ts")));
    }

    const projenrcTs = readProjenrcTsFile(project);
    if (projenrcTs) {
      patterns.add(prefix(rel, projenrcTs));
    }
  }

  return [...patterns].sort();
}

/**
 * Read the `tsconfig.include` array from a projen Project, if present.
 * Returns `undefined` for projects without a tsconfig component.
 */
function readTsconfigInclude(
  project: Project,
): ReadonlyArray<string> | undefined {
  const tsconfig = (project as WithTsconfig).tsconfig;
  if (!tsconfig) {
    return undefined;
  }
  return tsconfig.include;
}

/**
 * Return the project's projen-managed projenrc path when it is a TypeScript
 * file (`.projenrc.ts`). projen keeps `.projenrc.ts` out of every
 * `tsconfig.include` (it is registered in eslint's `allowDefaultProject`
 * instead), so we read it from projen's `Projenrc` component. Returns
 * `undefined` for a JavaScript projenrc or when none is present.
 */
function readProjenrcTsFile(project: Project): string | undefined {
  const filePath = ProjenrcFile.of(project)?.filePath;
  return filePath?.endsWith(".ts") ? filePath : undefined;
}

/**
 * Join a project-relative prefix with a glob entry using POSIX separators so
 * rendered rule files are stable across platforms. An empty `rel` means the
 * detected project is the root itself — return the entry unchanged.
 */
function prefix(rel: string, entry: string): string {
  if (!rel) {
    return entry;
  }
  return path.posix.join(rel, entry);
}
